import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { User, Shield, Info } from 'lucide-react'
import { api } from '@/utils/api'
import { formatChatTime, formatChatDate } from '@/utils/datetime'
import { Message } from '@/types'

export default function SharedChat() {
  const { shareId } = useParams<{ shareId: string }>()
  const [messages, setMessages] = useState<Message[]>([])
  const [userName, setUserName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchSharedChat = async () => {
      if (!shareId) {
        setError('No share link provided.')
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        let responseData = await api.getSharedConversation(shareId)
        
        // Handle potentially wrapped response (some APIs wrap in { data: ... })
        const data = (responseData as any)?.data && (responseData as any)?.conversation === undefined 
          ? (responseData as any).data 
          : responseData;

        console.log('Shared chat API processed data:', {
          type: typeof data,
          isNull: data === null,
          keys: data ? Object.keys(data) : [],
          data: data
        })

        if (!data || !data.conversation) {
          throw new Error(`Malformed or missing shared chat data (received keys: ${data ? Object.keys(data).join(', ') : 'none'})`)
        }
        setMessages(data.messages || [])
        setUserName(data.conversation.userName || 'User')
      } catch (err) {
        console.error('Failed to load shared chat:', err)
        setError('This shared chat link is no longer active or could not be found.')
      } finally {
        setLoading(false)
      }
    }

    fetchSharedChat()
  }, [shareId])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading chat history...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100">
          <div className="h-16 w-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Info className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Unavailable</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <a 
            href="/"
            className="inline-block bg-primary-600 text-white font-bold py-3 px-8 rounded-xl hover:bg-primary-700 transition-all"
          >
            Return Home
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Public Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center">
              <User className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Chat with {userName}</h1>
              <div className="flex items-center text-xs text-gray-500">
                <Shield className="h-3 w-3 mr-1" />
                <span>View-only Archive</span>
              </div>
            </div>
          </div>
          <div className="hidden sm:block">
            <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-full border border-green-100 uppercase tracking-wider">
              Public Link
            </span>
          </div>
        </div>
      </header>

      {/* Message List */}
      <main className="flex-1 max-w-4xl w-full mx-auto p-4 space-y-6">
        {messages.map((message, index) => {
          const isUser = message.sender === 'USER'
          const showDate = index === 0 || formatChatDate(messages[index-1].createdAt) !== formatChatDate(message.createdAt)
          
          return (
            <div key={message.id} className="space-y-4">
              {showDate && (
                <div className="flex justify-center">
                  <span className="px-4 py-1.5 bg-gray-200/50 text-gray-600 text-[10px] md:text-xs font-bold rounded-full uppercase tracking-widest backdrop-blur-sm">
                    {formatChatDate(message.createdAt)}
                  </span>
                </div>
              )}
              
              <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] sm:max-w-[70%] group`}>
                  <div className={`flex items-center mb-1.5 space-x-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
                    {!isUser && (
                      <span className="text-[10px] md:text-xs font-black text-primary-600 uppercase tracking-tighter">
                        {message.senderName}
                      </span>
                    )}
                    <span className="text-[10px] font-bold text-gray-400">
                      {formatChatTime(message.createdAt)}
                    </span>
                    {isUser && (
                      <span className="text-[10px] md:text-xs font-black text-gray-600 uppercase tracking-tighter">
                        {userName}
                      </span>
                    )}
                  </div>
                  
                  <div className={`relative p-3 md:p-4 rounded-2xl md:rounded-3xl shadow-sm border ${
                    isUser 
                      ? 'bg-white text-gray-800 rounded-tr-none border-gray-100' 
                      : 'bg-primary-600 text-white rounded-tl-none border-primary-500'
                  }`}>
                    {/* Quoted Message */}
                    {message.replyTo && (
                      <div className={`mb-3 p-3 rounded-xl border-l-4 text-xs ${
                        isUser 
                          ? 'bg-gray-50 border-gray-300 text-gray-600' 
                          : 'bg-primary-700 border-primary-300 text-primary-100'
                      }`}>
                        <p className="font-bold mb-1 uppercase tracking-wider text-[10px]">
                          {message.replyTo.sender === 'USER' ? (userName || 'User') : (message.replyTo.sender === 'ADMIN' ? 'Support' : message.replyTo.sender)}
                        </p>
                        <p className="line-clamp-2 italic opacity-90">{message.replyTo.content}</p>
                      </div>
                    )}

                    {message.imageUrl && (
                      <div className="mb-3 rounded-xl overflow-hidden border border-black/5">
                        <img 
                          src={message.imageUrl.startsWith('http') ? message.imageUrl : `${(import.meta.env.VITE_API_URL || 'http://localhost:3000/api').replace(/\/api$/, '')}${message.imageUrl}`} 
                          alt="Shared image" 
                          className="max-w-full h-auto cursor-pointer hover:scale-[1.02] transition-transform duration-300"
                        />
                      </div>
                    )}
                    <p className="text-sm md:text-base leading-relaxed whitespace-pre-wrap font-medium">
                      {message.content}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </main>

      {/* Footer Info */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-auto">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500 font-medium">
            This is a secure, view-only archive of a support conversation. 
            Interaction is disabled for public shared links.
          </p>
        </div>
      </footer>
    </div>
  )
}
